import React, { useState, useEffect, useRef } from 'react';
import useUser from '../hooks/useUser.js';
import { getReactionSummary, getReactionCsrfToken, setPageReaction } from '../helpers/api/pageReactions.js';

import '../styles/pageReactions.css';

const PageReactions = ({
    pageIndex,
    prompt = "Was this page helpful?",
    showDislike = true
}) => {
    const { logout } = useUser();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({ like: 0, dislike: 0 });
    const [ownReaction, setOwnReaction] = useState(null);
    const [csrfToken, setCsrfToken] = useState(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [summary, { token }] = await Promise.all([
                    getReactionSummary({ pageIndex, logout }),
                    getReactionCsrfToken({ logout })
                ]);

                if(isMounted.current) {
                    setCounts(summary.counts || { like: 0, dislike: 0 });
                    setOwnReaction(summary.ownReaction || null);
                    setCsrfToken(token);
                }
            }
            catch (error) {
                console.error(error);
            }
            isMounted.current && setLoading(false);
        })();
    }, [pageIndex]);

    const react = async (type) => {
        if(!csrfToken || loading) {
            return;
        }
        try {
            const response = await setPageReaction({ pageIndex, type, csrfToken, logout });
            if(isMounted.current && response) {
                setCounts(response.counts || counts);
                setOwnReaction(response.ownReaction ?? null);
            }
        }
        catch (error) {
            console.error(error);
        }
    };

    if(!pageIndex) {
        return null;
    }

    return (
        <div className="page-reactions">
            { prompt && <span className="prompt">{prompt}</span> }

            <div className="buttons">
                <button
                    type="button"
                    className={`button ${ownReaction === "like" ? "active" : ""}`}
                    disabled={loading}
                    aria-pressed={ownReaction === "like"}
                    aria-label="Like"
                    onClick={() => react("like")}
                >
                    <span
                        className="icon"
                        style={{
                            maskImage: `url(${process.env.PUBLIC_URL}/assets/icons/reactions/thumb.svg)`
                        }}
                    />
                    <span className="count">{counts.like || 0}</span>
                </button>

                {
                    showDislike && (
                        <button
                            type="button"
                            className={`button ${ownReaction === "dislike" ? "active" : ""}`}
                            disabled={loading}
                            aria-pressed={ownReaction === "dislike"}
                            aria-label="Dislike"
                            onClick={() => react("dislike")}
                        >
                            <span
                                className="icon dislike"
                                style={{
                                    maskImage: `url(${process.env.PUBLIC_URL}/assets/icons/reactions/thumb.svg)`
                                }}
                            />
                            <span className="count">{counts.dislike || 0}</span>
                        </button>
                    )
                }
            </div>
        </div>
    );
};

export default PageReactions;
